import Image from '@tiptap/extension-image';
import { getAttributes } from '@tiptap/core';
import { NodeSelection } from '@tiptap/pm/state';

type Alignment = 'left' | 'center' | 'right';

const withoutTextAlign = (style: string) =>
  style
    .split(';')
    .map((d) => d.trim())
    .filter((d) => d && !/^text-align\s*:/i.test(d))
    .join('; ');

/** Declarations the alignment margins replace, so the two never disagree on the same box. */
const withoutAlignmentBox = (style: string) =>
  style
    .split(';')
    .map((d) => d.trim())
    .flatMap((d) => {
      const margin = d.match(/^margin\s*:\s*(.+)$/i);
      if (!margin) return [d];
      const parts = margin[1].split(/\s+/);
      return [`margin-top: ${parts[0]}`, `margin-bottom: ${parts[2] ?? parts[0]}`];
    })
    .filter((d) => d && !/^(margin(-left|-right)?|display|text-align)\s*:/i.test(d))
    .join('; ');

const alignmentMargins: Record<Alignment, string> = {
  left: 'margin-left: 0; margin-right: auto;',
  right: 'margin-left: auto; margin-right: 0;',
  center: 'margin-left: auto; margin-right: auto;'
};

/**
 * How the node view's box sits in the line, from what the document says.
 *
 * A chosen alignment is a block with auto margins. With none chosen the image keeps the
 * layout it was written with: a block image sits where a block sits, at the left, and an
 * inline one stays inline so its parent's text-align places it, which is how the PDF
 * places it.
 */
export const imageBoxStyle = (attrs: {
  textAlign?: string | null;
  style?: string | null;
}): string => {
  const alignment = attrs.textAlign as Alignment | null | undefined;
  if (alignment && alignment in alignmentMargins) {
    return `display: block; width: fit-content; ${alignmentMargins[alignment]}`;
  }
  if (/display\s*:\s*block/i.test(attrs.style ?? '')) {
    return 'display: block; width: fit-content; margin-left: 0; margin-right: 0;';
  }
  return 'display: inline-block; width: fit-content;';
};

/** An image with nothing to load is a placeholder; one that names a file is a picture. */
export const hasLoadableSource = (src: string | null | undefined) => {
  const source = src?.trim();
  if (!source) return false;
  if (/^(https?:|data:image\/|blob:|\/)/i.test(source)) return true;
  // Relative paths resolve in the browser; a bare placeholder label names no asset.
  return !/^[\w+.-]+:/.test(source) && /[/.]/.test(source) && !/[[\]{}]/.test(source);
};

const loadedImageClass =
  'max-w-full transition-all group-hover:ring-2 group-hover:ring-primary-400';
const placeholderImageClass = 'max-w-full opacity-50';

/**
 * The alignment an image's margins express. A generated document centers a logo with
 * `margin: 0 auto` as often as with the longhands, and the print engine reads both, so the
 * editor has to as well or the same image lands in two places.
 */
export const alignmentFromMargins = (style: string): 'left' | 'center' | 'right' | null => {
  let left: string | undefined;
  let right: string | undefined;
  for (const declaration of style.split(';')) {
    const [rawName, ...rest] = declaration.split(':');
    const name = rawName?.trim().toLowerCase();
    const value = rest.join(':').trim().toLowerCase();
    if (!name || !value) continue;
    if (name === 'margin') {
      const parts = value.split(/\s+/);
      // One value sets every side; two or three set the sides with the second; four go clockwise.
      right = parts.length === 1 ? parts[0] : parts[1];
      left = parts.length === 4 ? parts[3] : right;
    } else if (name === 'margin-left') {
      left = value;
    } else if (name === 'margin-right') {
      right = value;
    }
  }
  if (left === 'auto' && right === 'auto') return 'center';
  if (left === 'auto') return 'right';
  if (right === 'auto') return 'left';
  return null;
};

export const CustomImage = Image.extend({
  addAttributes() {
    return {
      ...this.parent?.(),
      src: {
        default: null,
        parseHTML: (element) => element.getAttribute('src'),
        renderHTML: (attributes) => {
          if (!attributes.src) {
            return {};
          }
          return {
            src: attributes.src
          };
        }
      },
      alt: {
        default: null,
        parseHTML: (element) => element.getAttribute('alt'),
        renderHTML: (attributes) => {
          if (!attributes.alt) {
            return {};
          }
          return {
            alt: attributes.alt
          };
        }
      },
      title: {
        default: null,
        parseHTML: (element) => element.getAttribute('title'),
        renderHTML: (attributes) => {
          if (!attributes.title) {
            return {};
          }
          return {
            title: attributes.title
          };
        }
      },
      'data-image-id': {
        default: null,
        parseHTML: (element) => element.getAttribute('data-image-id'),
        renderHTML: (attributes) => {
          if (!attributes['data-image-id']) {
            return {};
          }
          return {
            'data-image-id': attributes['data-image-id']
          };
        }
      },
      'data-image-identifier': {
        default: null,
        parseHTML: (element) => element.getAttribute('data-image-identifier'),
        renderHTML: (attributes) => {
          if (!attributes['data-image-identifier']) {
            return {};
          }
          return {
            'data-image-identifier': attributes['data-image-identifier']
          };
        }
      },
      'data-logo-variant': {
        default: null,
        parseHTML: (element) => element.getAttribute('data-logo-variant'),
        renderHTML: (attributes) =>
          attributes['data-logo-variant']
            ? { 'data-logo-variant': attributes['data-logo-variant'] }
            : {}
      },
      width: {
        default: null,
        parseHTML: (element) => element.getAttribute('width'),
        renderHTML: (attributes) => {
          if (!attributes.width) {
            return {};
          }
          return {
            width: attributes.width
          };
        }
      },
      height: {
        default: null,
        parseHTML: (element) => element.getAttribute('height'),
        renderHTML: (attributes) => {
          if (!attributes.height) {
            return {};
          }
          return {
            height: attributes.height
          };
        }
      }
    };
  },

  addNodeView() {
    return ({ node, HTMLAttributes, getPos, editor }) => {
      const container = document.createElement('div');
      const baseContainerClass = 'relative group cursor-pointer';
      container.className = baseContainerClass;
      container.style.cssText = imageBoxStyle(
        node.attrs as { textAlign?: string | null; style?: string | null }
      );
      container.setAttribute('data-drag-handle', '');

      const img = document.createElement('img');
      img.src = node.attrs.src || '';
      img.alt = node.attrs.alt || '';
      img.draggable = false;
      // The image's own declarations (a width, a bottom margin) are what the PDF prints it with.
      const ownStyle = (node.attrs as { style?: string | null }).style;
      if (ownStyle) {
        img.style.cssText = withoutTextAlign(ownStyle);
      }

      if (node.attrs.title) {
        img.title = node.attrs.title;
      }

      if (node.attrs.width) {
        img.width = parseInt(node.attrs.width);
      }

      if (node.attrs.height) {
        img.height = parseInt(node.attrs.height);
      }

      if (node.attrs['data-image-id']) {
        img.setAttribute('data-image-id', node.attrs['data-image-id']);
      }

      if (node.attrs['data-image-identifier']) {
        img.setAttribute('data-image-identifier', node.attrs['data-image-identifier']);
      }

      const isPlaceholderImage = () => !hasLoadableSource(node.attrs.src);

      // Check if this is a placeholder initially
      let isPlaceholder = isPlaceholderImage();

      // Set image className - adjust based on whether it's a placeholder
      if (isPlaceholder) {
        img.className = placeholderImageClass;
        container.classList.add(
          'border-2',
          'border-dashed',
          'border-neutral-300',
          'dark:border-neutral-600',
          'bg-neutral-50',
          'dark:bg-neutral-900'
        );
        container.classList.remove('group');
      } else {
        img.className = loadedImageClass;
      }

      // Update placeholder status when image loads or errors
      img.addEventListener('load', () => {
        const wasPlaceholder = isPlaceholder;
        isPlaceholder = isPlaceholderImage();
        if (wasPlaceholder !== isPlaceholder) {
          if (isPlaceholder) {
            img.className = placeholderImageClass;
            container.classList.add(
              'border-2',
              'border-dashed',
              'border-neutral-300',
              'dark:border-neutral-600',
              'bg-neutral-50',
              'dark:bg-neutral-900'
            );
            container.classList.remove('group');
          } else {
            img.className = loadedImageClass;
            container.classList.remove(
              'border-2',
              'border-dashed',
              'border-neutral-300',
              'dark:border-neutral-600',
              'bg-neutral-50',
              'dark:bg-neutral-900'
            );
            container.classList.add('group');
          }
        }
      });

      img.addEventListener('error', () => {
        // Image failed to load - treat as placeholder
        isPlaceholder = true;
        img.className = placeholderImageClass;
        container.classList.add(
          'border-2',
          'border-dashed',
          'border-red-300',
          'dark:border-red-500/30',
          'bg-red-50',
          'dark:bg-red-500/10'
        );
        container.classList.remove('group');
      });

      // Apply any additional HTML attributes
      Object.keys(HTMLAttributes).forEach((key) => {
        if (
          key !== 'src' &&
          key !== 'alt' &&
          key !== 'title' &&
          key !== 'width' &&
          key !== 'height' &&
          key !== 'data-image-id' &&
          key !== 'data-image-identifier'
        ) {
          img.setAttribute(key, HTMLAttributes[key]);
        }
      });

      // Remove (X) button - top right, for both placeholder and valid images
      const removeBtn = document.createElement('button');
      removeBtn.type = 'button';
      removeBtn.setAttribute('aria-label', 'Remove image');
      removeBtn.className =
        'absolute top-1 right-1 z-10 p-1 rounded-md text-neutral-400 dark:text-neutral-500 hover:text-neutral-700 dark:hover:text-neutral-300 hover:bg-neutral-200 dark:hover:bg-white/10 transition-colors';
      removeBtn.innerHTML = `
        <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" class="w-4 h-4">
          <path stroke-linecap="round" stroke-linejoin="round" d="M6 18 18 6M6 6l12 12" />
        </svg>
      `;
      removeBtn.addEventListener('click', (e) => {
        e.preventDefault();
        e.stopPropagation();
        if (typeof getPos === 'function') {
          const pos = getPos();
          if (typeof pos === 'number') {
            editor.commands.setNodeSelection(pos);
            editor.commands.deleteSelection();
          }
        }
      });
      container.appendChild(removeBtn);

      // Create resize handles
      const resizeHandles = document.createElement('div');
      resizeHandles.className =
        'absolute inset-0 opacity-0 group-hover:opacity-100 pointer-events-none';

      // Corner handles
      const corners = ['nw', 'ne', 'sw', 'se'];
      const cornerPositions = {
        nw: 'top-0 left-0 -translate-x-1/2 -translate-y-1/2',
        ne: 'top-0 right-0 translate-x-1/2 -translate-y-1/2',
        sw: 'bottom-0 left-0 -translate-x-1/2 translate-y-1/2',
        se: 'bottom-0 right-0 translate-x-1/2 translate-y-1/2'
      };

      corners.forEach((corner) => {
        const handle = document.createElement('div');
        handle.className = `absolute ${cornerPositions[corner]} w-3 h-3 bg-white dark:bg-neutral-800 border-2 border-primary-500 rounded-full cursor-${corner}-resize pointer-events-auto`;
        handle.setAttribute('data-resize-handle', corner);

        let startX = 0;
        let startWidth = 0;
        let startHeight = 0;

        handle.addEventListener('mousedown', (e) => {
          e.preventDefault();
          e.stopPropagation();

          startX = e.clientX;
          const bounds = img.getBoundingClientRect();
          startWidth = bounds.width || img.width || img.naturalWidth;
          startHeight = bounds.height || img.height || img.naturalHeight;

          const aspectRatio = startWidth / startHeight;

          const handleMouseMove = (moveEvent: MouseEvent) => {
            const deltaX = moveEvent.clientX - startX;

            let newWidth = startWidth;
            let newHeight = startHeight;

            // Calculate new dimensions based on corner
            if (corner === 'se' || corner === 'ne') {
              newWidth = startWidth + deltaX;
            } else {
              newWidth = startWidth - deltaX;
            }

            // Maintain aspect ratio
            newHeight = newWidth / aspectRatio;

            // Minimum size
            if (newWidth < 50) newWidth = 50;
            if (newHeight < 50) newHeight = 50;

            // Update image size
            img.width = Math.round(newWidth);
            img.height = Math.round(newHeight);
            img.style.width = `${img.width}px`;
            img.style.height = `${img.height}px`;
          };

          const handleMouseUp = () => {
            document.removeEventListener('mousemove', handleMouseMove);
            document.removeEventListener('mouseup', handleMouseUp);

            // Update node attributes
            if (typeof getPos === 'function') {
              const pos = getPos();
              if (typeof pos === 'number') {
                editor
                  .chain()
                  .setNodeSelection(pos)
                  .setImageSize({ width: img.width, height: img.height })
                  .run();
              }
            }
          };

          document.addEventListener('mousemove', handleMouseMove);
          document.addEventListener('mouseup', handleMouseUp);
        });

        resizeHandles.appendChild(handle);
      });

      const errorEl = document.createElement('div');
      errorEl.className = 'hidden text-xs text-red-600 dark:text-red-400 mt-1';
      container.appendChild(errorEl);

      let errorTimer: ReturnType<typeof setTimeout> | null = null;
      const showEmptyFileError = (fileName: string) => {
        errorEl.textContent = `"${fileName}" is empty. Please upload a readable image.`;
        errorEl.classList.remove('hidden');
        if (errorTimer) clearTimeout(errorTimer);
        errorTimer = setTimeout(() => {
          errorEl.classList.add('hidden');
          errorEl.textContent = '';
        }, 4000);
      };

      const openUploadDialog = () => {
        const pos = typeof getPos === 'function' ? getPos() : undefined;
        if (typeof pos !== 'number') return;
        const input = document.createElement('input');
        input.type = 'file';
        input.accept = 'image/*';
        input.onchange = async (event) => {
          const file = (event.target as HTMLInputElement).files?.[0];
          if (!file) return;
          if (file.size === 0) {
            showEmptyFileError(file.name);
            return;
          }
          const uploadEvent = new CustomEvent('image-placeholder-upload', {
            detail: {
              file,
              pos,
              identifier: node.attrs['data-image-identifier'] || node.attrs.alt || ''
            },
            bubbles: true,
            composed: true
          });
          container.dispatchEvent(uploadEvent);
        };
        input.click();
      };

      // Click handler - placeholder: open upload; valid image: show resize; ignore clicks on X button
      container.addEventListener('click', (e) => {
        e.stopPropagation();
        if ((e.target as HTMLElement).closest('button[aria-label="Remove image"]')) return;
        if (typeof getPos === 'function') {
          const pos = getPos();
          if (typeof pos === 'number') {
            editor.commands.setNodeSelection(pos);

            if (isPlaceholderImage()) {
              openUploadDialog();
              return;
            }

            // For valid images, show resize controls
            const currentWidth = node.attrs.width ? parseInt(node.attrs.width) : img.naturalWidth;
            const currentHeight = node.attrs.height
              ? parseInt(node.attrs.height)
              : img.naturalHeight;

            const event = new CustomEvent('image-selected', {
              detail: {
                pos,
                width: currentWidth,
                height: currentHeight,
                node
              },
              bubbles: true,
              composed: true
            });
            container.dispatchEvent(event);
          }
        }
      });

      container.appendChild(img);
      container.appendChild(resizeHandles);

      return {
        dom: container,
        contentDOM: undefined,
        update: (updatedNode) => {
          if (updatedNode.type.name !== 'image') {
            return false;
          }

          // Update image attributes when node changes
          img.src = updatedNode.attrs.src || '';
          img.alt = updatedNode.attrs.alt || '';

          if (updatedNode.attrs.title) {
            img.title = updatedNode.attrs.title;
          } else {
            img.removeAttribute('title');
          }

          if (updatedNode.attrs.width) {
            img.width = parseInt(updatedNode.attrs.width);
          } else {
            img.removeAttribute('width');
          }

          if (updatedNode.attrs.height) {
            img.height = parseInt(updatedNode.attrs.height);
          } else {
            img.removeAttribute('height');
          }

          if (updatedNode.attrs['data-image-id']) {
            img.setAttribute('data-image-id', updatedNode.attrs['data-image-id']);
          } else {
            img.removeAttribute('data-image-id');
          }

          if (updatedNode.attrs['data-image-identifier']) {
            img.setAttribute('data-image-identifier', updatedNode.attrs['data-image-identifier']);
          } else {
            img.removeAttribute('data-image-identifier');
          }

          // Update placeholder status after attributes change
          // Re-check if image is still a placeholder based on updated attributes
          const checkIsPlaceholderNow = () => !hasLoadableSource(updatedNode.attrs.src);
          const updatedOwnStyle = (updatedNode.attrs as { style?: string | null }).style;
          img.style.cssText = updatedOwnStyle ? withoutTextAlign(updatedOwnStyle) : '';

          const updatePlaceholderStyling = (isPlaceholderNow: boolean) => {
            if (isPlaceholderNow) {
              img.className = placeholderImageClass;
              container.classList.add(
                'border-2',
                'border-dashed',
                'border-neutral-300',
                'dark:border-neutral-600',
                'bg-neutral-50',
                'dark:bg-neutral-900'
              );
              container.classList.remove('group');
            } else {
              img.className = loadedImageClass;
              container.classList.remove(
                'border-2',
                'border-dashed',
                'border-neutral-300',
                'dark:border-neutral-600',
                'bg-neutral-50',
                'dark:bg-neutral-900',
                'border-red-300',
                'dark:border-red-500/30',
                'bg-red-50',
                'dark:bg-red-500/10'
              );
              container.classList.add('group');
            }
          };

          // If image is already loaded, check immediately
          if (img.complete && img.naturalWidth > 0) {
            // Image loaded successfully - check if it's still a placeholder
            const isPlaceholderNow = checkIsPlaceholderNow();
            updatePlaceholderStyling(isPlaceholderNow);
          } else if (img.complete && img.naturalWidth === 0) {
            // Image failed to load - treat as placeholder
            updatePlaceholderStyling(true);
          } else {
            // Image is still loading - wait for load or error
            const onLoad = () => {
              const isPlaceholderNow = checkIsPlaceholderNow();
              updatePlaceholderStyling(isPlaceholderNow);
              img.removeEventListener('load', onLoad);
              img.removeEventListener('error', onError);
            };
            const onError = () => {
              updatePlaceholderStyling(true);
              img.removeEventListener('load', onLoad);
              img.removeEventListener('error', onError);
            };
            // Add listeners (they will be removed in the handlers)
            img.addEventListener('load', onLoad);
            img.addEventListener('error', onError);
          }

          container.style.cssText = imageBoxStyle(
            updatedNode.attrs as { textAlign?: string | null; style?: string | null }
          );

          return true;
        }
      };
    };
  },

  parseHTML() {
    // Use parent's parseHTML to ensure src and other base attributes are parsed
    const parentRules = this.parent?.() || [];

    // Add custom parsing to extract textAlign from margin-based styles
    // IMPORTANT: Put our rule FIRST so it takes precedence over parent rules
    return [
      {
        tag: 'img',
        getAttrs: (element) => {
          const attrs: Record<string, string> = {};

          const style = element.getAttribute('style');
          const alignment = style ? alignmentFromMargins(style) : null;
          if (alignment) {
            attrs.textAlign = alignment;
          }

          return attrs;
        }
      },
      ...parentRules
    ];
  },

  renderHTML({ node, HTMLAttributes }) {
    // Ensure proper HTML output (just the img tag, not the wrapper div from NodeView)
    // Merge node attributes to ensure src and dimensions are included
    const baseStyle = (node.attrs as { style?: string | null }).style ?? '';
    const textAlign = (node.attrs as { textAlign?: string }).textAlign;

    // Images carry alignment as margins, not text-align. An image nobody aligned keeps the
    // style it was written with.
    let alignStyle = '';
    if (textAlign === 'left') {
      alignStyle = 'display:block;margin-right:auto;margin-left:0;';
    } else if (textAlign === 'right') {
      alignStyle = 'display:block;margin-left:auto;margin-right:0;';
    } else if (textAlign === 'center') {
      alignStyle = 'display:block;margin-left:auto;margin-right:auto;';
    }

    // The alignment owns the box; everything else the image said (a width, a bottom margin)
    // travels with it, or saving a document would strip its logos to natural size.
    const own = baseStyle ? (alignStyle ? withoutAlignmentBox(baseStyle) : baseStyle) : '';
    const style =
      [own, alignStyle]
        .map((part) => part.trim().replace(/;$/, ''))
        .filter(Boolean)
        .join('; ') || undefined;

    // Explicitly set style to override any style from HTMLAttributes
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const { style: _unused, ...restHTMLAttributes } = HTMLAttributes;

    return [
      'img',
      {
        ...restHTMLAttributes,
        src: node.attrs.src,
        alt: node.attrs.alt,
        title: node.attrs.title,
        width: node.attrs.width,
        height: node.attrs.height,
        'data-image-id': node.attrs['data-image-id'],
        'data-image-identifier': node.attrs['data-image-identifier'],
        style: style || undefined
      }
    ];
  },

  addCommands() {
    return {
      ...this.parent?.(),
      setImageSize:
        (options: { width: number; height: number }) =>
        ({ commands, state }) => {
          const style = document.createElement('span').style;
          style.cssText = (getAttributes(state, 'image').style as string | null) ?? '';
          style.setProperty('width', `${options.width}px`);
          style.setProperty('height', `${options.height}px`);
          return commands.updateAttributes('image', {
            width: String(options.width),
            height: String(options.height),
            style: style.cssText
          });
        },
      deleteImage:
        () =>
        ({ commands, state }) => {
          const { selection } = state;

          if (selection instanceof NodeSelection && selection.node.type.name === 'image') {
            return commands.deleteSelection();
          }

          return false;
        }
    };
  },

  addKeyboardShortcuts() {
    return {
      Delete: () => {
        const { state } = this.editor;
        const { selection } = state;

        if (selection instanceof NodeSelection && selection.node.type.name === 'image') {
          return this.editor.commands.deleteSelection();
        }

        return false;
      },
      Backspace: () => {
        const { state } = this.editor;
        const { selection } = state;

        if (selection instanceof NodeSelection && selection.node.type.name === 'image') {
          return this.editor.commands.deleteSelection();
        }

        return false;
      }
    };
  }
});
